import React from "react";
import { useSelector } from "react-redux";
import { Badge, BadgeColor, Card, DisabledText, ExternalLinkIcon, Inline, Stack, SubHeadline } from "@KristianIV/common-ui";
import { ExternalButton } from "../shell/RouteButton";
import { ApplicationState } from "../reducers";
import { Scout, ScoutState } from "./model/scout";
import { REVIEW_ENROLLMENT_URL, reviewScoutUrl } from "./reviewLinks";
import styles from "./OrganisationPage.module.css";

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const enrolledAtToDate = (enrolledAt: string) => {
  const date = new Date(enrolledAt);
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

// The audio state comes from the live scout status instead of the fast track
// enrollment, which only holds a snapshot of muted.
const audioBadge = (state: ScoutState): { label: string; color: BadgeColor } => {
  switch (state) {
    case ScoutState.Listening:
      return { label: "Listening", color: "green" };
    case ScoutState.Ready:
      return { label: "Muted", color: "blue" };
    default:
      return { label: "Not listening", color: "disabled" };
  }
};

const openInReview = (deviceId: string) => window.open(reviewScoutUrl(deviceId), "_blank", "noopener,noreferrer");

/**
 * A scout's fast-track enrolment: group, state, tags and who enrolled it. Enrolment is managed in the Review
 * app, so an enrolled scout's card opens it there and an unenrolled scout gets a link to enrol it.
 */
const FastTrack = ({ scout, state }: { scout: Scout; state: ScoutState }) => {
  const deviceId = scout.status.deviceId;
  const fastTrackScout = useSelector((appState: ApplicationState) =>
    appState.organisation.fastTrackScouts.find((f) => f.deviceId === deviceId)
  );
  const audio = audioBadge(state);

  if (!fastTrackScout) {
    return (
      <Card interactive={false} padded>
        <SubHeadline>Fast track</SubHeadline>
        <Stack gap="md" align="start">
          <DisabledText>This scout is not in fast track.</DisabledText>
          <ExternalButton href={REVIEW_ENROLLMENT_URL} icon={<ExternalLinkIcon />}>
            Enroll this device
          </ExternalButton>
        </Stack>
      </Card>
    );
  }

  return (
    <Card padded onClick={() => openInReview(deviceId)} className={styles.reviewCard}>
      <Stack gap="lg">
        <Inline justify="between" wrap={false}>
          <SubHeadline noMargin>Fast track</SubHeadline>
          <span className={styles.reviewHint} title="Open in Review">
            <ExternalLinkIcon />
          </span>
        </Inline>
        <Stack gap="md" align="start">
          <Inline>
            <span className={styles.fastTrackGroup}>{fastTrackScout.group}</span>
            <Badge color={fastTrackScout.paused ? "amber" : "green"}>{fastTrackScout.paused ? "Paused" : "Active"}</Badge>
            <Badge color={audio.color}>{audio.label}</Badge>
          </Inline>
          {fastTrackScout.tags.length > 0 && (
            <Inline gap="xs">
              {fastTrackScout.tags.map((tag) => (
                <Badge key={tag}>{tag}</Badge>
              ))}
            </Inline>
          )}
          <DisabledText>
            Enrolled by {fastTrackScout.enrolledBy} · {enrolledAtToDate(fastTrackScout.enrolledAt)}
          </DisabledText>
        </Stack>
      </Stack>
    </Card>
  );
};

export default FastTrack;
